import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import './index.css'

// Scrub user-identifying path segments from URLs before sending to Sentry.
// /profile/<uuid> and /manga/<id> and /anime/<id> are identifying when combined with session data.
function scrubUrl(u) {
  if (typeof u !== 'string') return u
  return u
    .replace(/\/profile\/[^/?#]+/g, '/profile/[redacted]')
    .replace(/\/manga\/[^/?#]+/g,   '/manga/[redacted]')
    .replace(/\/anime\/[^/?#]+/g,   '/anime/[redacted]')
    .replace(/\/read\/[^/?#]+\/[^/?#]+/g, '/read/[redacted]/[redacted]')
}

Sentry.init({
  dsn: 'https://f5b16f798d461611d07b3de67eb26245@o4511184542302208.ingest.de.sentry.io/4511184547414096',
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.2,
  sendDefaultPii: false,
  beforeSend(event) {
    if (event.request?.url) event.request.url = scrubUrl(event.request.url)
    if (event.request?.headers) delete event.request.headers.Cookie
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(b => ({
        ...b,
        data: b.data?.url ? { ...b.data, url: scrubUrl(b.data.url) } : b.data,
      }))
    }
    return event
  },
})

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>
)

// Service worker auto-update. The plugin's `autoUpdate` registerType installs
// a new SW in the background but the existing tab keeps running the old JS
// until the user manually refreshes — that's how a freshly-loaded user can
// see a stale build. Listen for `controllerchange` and reload once, so the
// new bundle takes over the moment it's ready.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  let reloading = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return
    reloading = true
    window.location.reload()
  })
}
